<script type="text/jsx">
import ConfigBase from '@/components/config-base';
import { BILLING_CYCLE, planTypes, trackingPlanTypes } from '@/common/constants';
import { getPlanUnitWithCycle, getPlanUnitWithQuantity, planUnitMap } from '@/common/product-unit';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import PlanDetails from '@/plans/ZOOM_MEETING/dialog/plan-details';
import _ from 'lodash';
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';
import { plansof } from '@/utils/chain';
import { mapGetters } from 'vuex';
import { computedPbxBasePlans, computedPbxTollFreePlans } from '@/common/service/computers/plans/pbx';
import { getZPCallingFeesText } from '@/plans/ZOOM_PHONE/helper/utils';
import { additionalConfigType, AdditionalConfigTypeEnums } from '@/plans/ZOOM_PHONE$ADDITIONAL/helper';

export default {
  name: 'ConfigProPlusBase',
  extends: ConfigBase,
  props: {
    showPlanDetails: Boolean,
    planDetailsLabel: String,
    hideComparisonTable: {
      type: Boolean,
      default: false,
    },
    showFullComparisonDialog: {
      type: Function,
      default: _.noop,
    },
  },
  data() {
    return {
      title: this.$t('billing.zone_pro_plus.title'),
      subtitle: '',
      planType: planTypes.ZONE_PRO_PLUS,
      logo: require('@/assets/plans/icon_ZM.svg'),
      showLearnMore: false,
      unit: planUnitMap.user,
      trackingPlanName: trackingPlanTypes.ZONE_PRO_PLUS,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'pbxBasePlansGroupByBillingCycle']),
    zoneProPlusPlanList() {
      return plansof(this.getSelectedPlans).type(this.planType).get();
    },
    cycleAndUsageFee() {
      const zoneProPlus = this.zoneProPlusPlanList?.[0];
      const pbxBasePlan = computedPbxBasePlans(this.getSelectedPlans)?.[0];
      return [zoneProPlus?.billing_cycle || BILLING_CYCLE.ANNUAL, pbxBasePlan?.plan_code];
    },
    cascadeCycleOptionList() {
      const cycleList = [BILLING_CYCLE.MONTHLY, BILLING_CYCLE.QUARTERLY, BILLING_CYCLE.ANNUAL, BILLING_CYCLE.TWO_YEARS, BILLING_CYCLE.THREE_YEARS];

      return _.map(cycleList, (cycle) => {
        return {
          value: cycle,
          options: _.map(this.pbxBasePlansGroupByBillingCycle[cycle], (plan) => {
            return {
              value: plan.plan_code,
              title: this.$t(getZPCallingFeesText(plan).title),
              subTitle: this.$money(plan.amount, { forceDiff: true }),
              description: this.$t(getZPCallingFeesText(plan).unit),
            };
          }),
        };
      });
    },
  },
  methods: {
    async showPlanDetailDialog() {
      if (this.$context.is.edit || this.$context.is.pro2prop) {
        return await this.showFullComparisonDialog();
      }
      this.$tracking.buyInteraction('plan details selected', this.trackingPlanName);
      await this.planDetailsDialog.confirm({ planCategory: ZOOM_MEETING_SUB_TYPES.PRO_PLUS, hideComparisonTable: this.hideComparisonTable });
      await this.showFullComparisonDialog().catch(()=>{
        if(this.$context.is.modal){
          this.showPlanDetailDialog()
        }
      })
    },
    mx_renderProPlusCallingPlanLabel() {
      return _.map(this.zoneProPlusPlanList, (plan) => {
        const pbxCategoryCode = plan?.pbxCategory;
        if (!pbxCategoryCode) return '';

        const planName = this.$t(`billing.pbx.cart_name.${pbxCategoryCode}`);
        const cycleLabel = getPlanUnitWithCycle(plan?.billing_cycle, this.unit);

        return <p class="opc-zm-base-card__collapse-pbx-item-row" key={plan.plan_code}>
          {planName} • {plan.cartInfo.selected_number} {getPlanUnitWithQuantity(this.unit)} • {this.$money(plan.amount)}{cycleLabel}
        </p>;
      });
    },
    mx_renderPbxTollFreePlanLabel() {
      const selectedTFPlanList = computedPbxTollFreePlans(this.getSelectedPlans);

      return _.map(selectedTFPlanList, (plan) => {
        const cycleLabel = getPlanUnitWithCycle(plan?.billing_cycle, planUnitMap.number);
        const planName = this.$t(`billing.pbx.cart_name.${plan.category}`);

        return <p class="opc-zm-base-card__collapse-pbx-item-row" key={plan.plan_code}>
          {planName} • {plan.cartInfo.selected_number} {getPlanUnitWithQuantity(planUnitMap.number)} • {this.$money(plan.amount)}{cycleLabel}
        </p>;
      });
    },
    mx_renderPbxBasePlanLabel() {
      const [billingCycle, usageFeePlanCode] = this.cycleAndUsageFee;
      const cycleOption = _.find(this.cascadeCycleOptionList, (x) => x.value === billingCycle);
      const usageFeeOption = _.find(cycleOption?.options, (x) => x.value === usageFeePlanCode);

      if (!usageFeeOption) return null;

      const usageFeeLabel = usageFeeOption ?
        `${usageFeeOption.title}  •  ${this.$t('billing.pbx.calling_fee_name.pay_you_go.MC')}  •  ${usageFeeOption.subTitle}${usageFeeOption.description}` : '';

      return <p class="opc-zm-base-card__collapse-pbx-item-row">{usageFeeLabel}</p>;
    },
    mx_renderSelectedList() {
      if (this.isExpanded) return;

      return <div class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset" slot="collapse">
        {this.mx_renderProPlusCallingPlanLabel()}
        {additionalConfigType.configType === AdditionalConfigTypeEnums.PN_AND_TF ? null : this.mx_renderPbxTollFreePlanLabel()}
        {this.mx_renderPbxBasePlanLabel()}
      </div>;
    },
  },
  created() {
    this.planDetailsDialog = new ServiceConfirm(PlanDetails);
  },
};
</script>

<style lang="scss">
.opc-zm-base-card {
  &__collapse-pbx-item-row {
    margin: 0px;
    font-size: 14px;
    font-weight: 500;
    line-height: 16.8px;
  }
}
</style>
